import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarProfileCard } from "@/shared/layout/SidebarProfileCard";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { parseDateValue } from "@/shared/components/CalendarDateField";

interface UserProfileCardProps {
  profile: PatientProfile;
}

const GENDER_LABEL: Record<string, string> = {
  male: "Male", female: "Female", other: "Other", prefer_not_to_say: "Undisclosed",
};

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function getFullNameInitials(name: string) {
  return name.split(" ").filter(Boolean).map((part) => part.charAt(0)).slice(0, 2).join("").toUpperCase();
}

function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = parseDateValue(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
}

function formatDob(dateOfBirth: string | null): string | null {
  if (!dateOfBirth) return null;
  const parsed = parseDateValue(dateOfBirth);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(parsed);
}

export function ProfileSwitcher({ profile }: UserProfileCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { dependents, activeDependent, setActiveDependentId } = useFamily();
  const [isOpen, setIsOpen] = useState(false);
  const activeDependentId = activeDependent?.id ?? null;
  const age = calculateAge(profile.dateOfBirth);
  const dob = formatDob(profile.dateOfBirth);
  const detailsLine = [profile.gender ? GENDER_LABEL[profile.gender] : null, age ? `Age ${age}` : null, dob]
    .filter(Boolean)
    .join(" · ");
  const isIncomplete = !profile.gender || !profile.dateOfBirth || !profile.address || !profile.phone || !profile.phoneVerified;
  const { avatarSrc } = usePatientProfile();
  const ownerName = `${profile.firstName} ${profile.lastName}`.trim();
  const activeName = activeDependent?.name ?? ownerName;
  const activeDetails = activeDependent
    ? `${activeDependent.relationship} · Age ${activeDependent.age}`
    : detailsLine || undefined;

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Caregiver/dedicated-profile module hidden from end users — clicking the profile
  // card now goes straight to the profile page instead of opening the profile-switcher
  // panel (which listed dependent profiles and an "Add Family Member" button).
  // const selectOwner = () => {
  //   if (activeDependentId === null) {
  //     navigate("/patient/profile");
  //   } else {
  //     setActiveDependentId(null);
  //     navigate("/patient");
  //   }
  //   setIsOpen(false);
  // };

  // const selectDependent = (dependentId: string) => {
  //   setActiveDependentId(dependentId);
  //   setIsOpen(false);
  //   navigate("/patient");
  // };

  return (
    <div className="relative">
      <SidebarProfileCard
        onClick={() => navigate("/patient/profile")}
        avatarSrc={activeDependent ? null : avatarSrc}
        avatarInitials={activeDependent ? getFullNameInitials(activeDependent.name) : getInitials(profile.firstName, profile.lastName)}
        avatarBg="bg-[#180047]"
        name={activeName}
        detailsLine={activeDetails}
        alertSlot={
          !activeDependent && isIncomplete && (
            <span className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#E4002B]/50 px-4 py-2 text-sm font-semibold !text-white">
              <Icon icon="lucide:octagon-alert" className="h-4 w-4 shrink-0 !text-white" />
              Incomplete Profile
            </span>
          )
        }
      />

      {/* Profile-switcher panel (dependents list + Add Family Member) hidden — caregiver/
          dedicated-profile module is temporarily hidden from end users. */}
      {/* {isOpen && (
        <div className="absolute left-3 right-3 top-full z-50 -mt-1 rounded-2xl border border-[#E5E7EB] bg-white p-3 shadow-[0_16px_36px_rgba(17,24,39,0.18)]">
          <p className="px-1 pb-2 text-xs font-medium text-[#6B7280]">Switch Profile</p>
          <Button
            variant="ghost"
            onClick={selectOwner}
            className={cn(
              "!flex !h-auto !w-full !items-center !justify-start !gap-3 !rounded-xl !px-2 !py-2 !text-left",
              activeDependentId === null ? "!bg-[#F9CED8] hover:!bg-[#F9CED8]" : "hover:!bg-[#F5F5F5]",
            )}
          >
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-bold !text-white ${activeDependentId === null ? "bg-[#180047]" : "bg-[#9187A8]"}`}>
              {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : getInitials(profile.firstName, profile.lastName)}
            </span>
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-[#180047]">{ownerName}</span><span className="block text-xs text-[#6B7280]">You</span></span>
            {activeDependentId === null && <Icon icon="lucide:check" className="h-4 w-4 rounded-full bg-[#F17893] p-0.5 !text-white" />}
          </Button>

          <div className="mt-1 grid gap-1">
            {dependents.map((dependent) => (
              <Button
                key={dependent.id}
                variant="ghost"
                onClick={() => selectDependent(dependent.id)}
                className={cn(
                  "!flex !h-auto !w-full !items-center !justify-start !gap-3 !rounded-xl !px-2 !py-2 !text-left",
                  activeDependentId === dependent.id ? "!bg-[#F9CED8] hover:!bg-[#F9CED8]" : "hover:!bg-[#F5F5F5]",
                )}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold !text-white ${activeDependentId === dependent.id ? "bg-[#180047]" : "bg-[#9187A8]"}`}>{getFullNameInitials(dependent.name)}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-[#180047]">{dependent.name}</span><span className="block text-xs capitalize text-[#6B7280]">{dependent.relationship}</span></span>
                {activeDependentId === dependent.id && <Icon icon="lucide:check" className="h-4 w-4 rounded-full bg-[#F17893] p-0.5 !text-white" />}
              </Button>
            ))}
          </div>

          <div className="mt-2 border-t border-[#E5E7EB] pt-2">
            <Button
              onClick={() => { setIsOpen(false); navigate("/patient/manage-family?add-family-member=1"); }}
              className="!flex !w-full !items-center !justify-center !gap-1.5 !rounded-lg !bg-[#F17893] !px-3 !py-2 !text-xs !font-semibold !text-white hover:!bg-[#E85D7C]"
            >
              <Icon icon="lucide:plus" className="h-3.5 w-3.5 !text-white" /> Add Family Member
            </Button>
          </div>
        </div>
      )} */}
    </div>
  );
}
